<template>
  <div v-if="selectedChatroom" class="sidebox pt-3">
    <!-- 顯示圖片的 modal -->
    <b-modal id="modal-showImg" ok-title="關閉" centered ok-only>
      <img style="width: 480px" :src="currentImg" />
    </b-modal>

    <!-- 自訂暱稱的 modal -->
    <b-modal
      id="modal-nickname"
      title="自訂暱稱"
      ok-title="儲存"
      cancel-title="取消"
      centered
      @ok="handleUpdateCustomerProfile"
      @hidden="handleCloseUpdateNickname"
    >
      <b-input
        type="text"
        class="form-control"
        maxlength="20"
        placeholder="自訂暱稱不會向對方公開"
        v-model="inputNickname"
      />
    </b-modal>

    <!-- 動態欄位編輯 modal -->
    <b-modal
      id="modal-edit-field"
      :title="((editingField && editingField.title) + (editingField && editingField.required ? '*' : '')) || '編輯'"
      ok-title="儲存"
      cancel-title="取消"
      centered
      @ok="handleSaveEditingField"
    >
      <div v-if="editingField">
        <small class="text-muted" v-if="editingField.memo">{{ editingField.memo }}</small>
        <b-form-input
          v-if="editingField.type === 'text'"
          @input="handleEditingFieldChanged"
          v-model="editingValue"
          :disabled="editingField.disabled"
        ></b-form-input>

        <b-form-select
          v-if="editingField.type === 'select'"
          @input="handleEditingFieldChanged"
          v-model="editingValue"
          :options="editingField.options"
          :disabled="editingField.disabled"
        ></b-form-select>

        <b-form-radio-group
          v-if="editingField.type === 'radio'"
          @input="handleEditingFieldChanged"
          v-model="editingValue"
          :options="editingField.options"
          :disabled="editingField.disabled"
        ></b-form-radio-group>

        <b-form-checkbox-group
          v-if="editingField.type === 'checkbox'"
          @input="handleEditingFieldChanged"
          v-model="editingValue"
          :options="editingField.options"
          :disabled="editingField.disabled"
        ></b-form-checkbox-group>

        <b-form-textarea
          v-if="editingField.type === 'textarea'"
          @input="handleEditingFieldChanged"
          v-model="editingValue"
          rows="5"
          class="liff-reservation-form__textarea"
        />

        <datepicker
          v-if="editingField.type === 'date'"
          @input="handleEditingFieldChanged"
          v-model="editingValue"
          bootstrap-styling
          format="yyyy-MM-dd"
          :language="zh"
        ></datepicker>

        <b-form-tags
          v-if="editingField.type === 'tags'"
          @input="handleEditingFieldChanged"
          v-model="editingValue"
          tag-variant="dark"
          remove-on-delete
          separator=" ,;"
        >
          <template #default="{ tags, inputAttrs, inputHandlers, removeTag }">
            <div class="d-flex flex-wrap align-items-center">
              <b-badge
                v-for="tag in tags"
                :key="tag"
                variant="dark"
                class="mr-2 mb-1"
              >
                {{ tag }}
                <button class="btn btn-xs p-0" @click="removeTag(tag)">
                  <i class="fa fa-remove text-white" />
                </button>
              </b-badge>

              <input
                v-bind="inputAttrs"
                v-on="inputHandlers"
                placeholder="輸入標籤"
                class="form-control border-0 shadow-none flex-grow-1"
              />
            </div>
          </template>
        </b-form-tags>

        <div class="text-danger mt-2" style="font-size: 0.75rem" v-if="validationErrorMessage">
          {{ validationErrorMessage }}
        </div>
      </div>
    </b-modal>

    <!-- 編輯標籤的 modal -->
    <b-modal
      id="modal-tags"
      title="編輯標籤"
      ok-title="儲存"
      cancel-title="取消"
      centered
      @ok="handleSaveTags"
    >
      <div class="border rounded p-1">
        <b-badge
          v-for="(tag, index) in tagsWithoutDuplicate"
          :key="tag"
          variant="dark"
          class="m-1"
        >
          {{ tag }}
          <button class="btn btn-xs p-0" @click="handleClickRemoveTag(index)">
            <i class="fa fa-remove text-white" />
          </button>
        </b-badge>
        <b-input
          type="text"
          class="border-0"
          maxlength="40"
          placeholder="輸入標籤"
          v-model="inputTag"
          @keyup.enter="handleEnterInputTag"
          @keyup="handleKeyUpInputTag"
          @blur="handleEnterInputTag"
        />
      </div>
    </b-modal>

    <!-- 成員&檔案 分頁 section -->
      <b-tabs
        class="h-100 d-flex flex-column overflow-y-hidden"
        v-if="showMemberListInDashboard || showFileListInDashboard"
        content-class="flex-fill overflow-y-auto overflow-x-hidden pl-2"
      >
        <b-tab
          title="成員"
          title-item-class="px-2"
          v-if="showMemberListInDashboard"
        >
          <b-list-group v-if="!selectedMember">
            <div class="text-center mt-3 mb-3 loading-spinner" v-if="loading">
              <b-spinner variant="secondary" small />
            </div>
            <div v-else>
              <ChatroomAddMember
                v-if="canShowAddMemberButton"
                :chatroom="selectedChatroom"
                :disabled="disabledAddMemberButton"
                @added="
                  fetchCustomers();
                  fetchStaffs();
                "
              ></ChatroomAddMember>

              <b-list-group-item
                class="d-flex align-items-center border-0"
                v-for="staff in staffs"
                :key="staff.id"
                href="#"
              >
                <b-avatar class="mr-1" :src="staff.avatar_url" :class="staff.is_enabled ? '' : 'gray-mask'"></b-avatar>
                <div
                  class="mr-auto font-weight-bold text-nowrap text-truncate pr-2"
                >
                  {{ staff.name }}
                </div>

                <div class="text-nowrap">
                  <b-button
                    v-if="
                      chatroomConfig.invite_staff_in_dashboard_permission_role &&
                      canShowRemoveMemberButton &&
                      (staff.role !== chatroomConfig.invite_staff_in_dashboard_permission_role)
                    "
                    size="xs"
                    pill
                    variant="danger"
                    class="p-1 mr-2"
                    @click="removeMember(staff)"
                  >
                    <i class="fa fa-times m-0"></i>
                  </b-button>
                </div>

                <!-- <i class="fa fa-angle-right" aria-hidden="true"></i> -->
              </b-list-group-item>

              <b-list-group-item
                class="d-flex align-items-center border-0"
                v-for="customer in customers"
                :key="customer.id"
                href="#"
                @click="handleSelectMember(customer.id)"
              >
                <b-avatar class="mr-1" :src="customer.avatar_url" :class="customer.is_enabled ? '' : 'gray-mask'"></b-avatar>
                <div
                  class="mr-auto font-weight-bold text-nowrap text-truncate pr-2"
                >
                  {{ customer.name }}
                </div>

                <div class="text-nowrap">
                  <!--移除客戶的功能暫時還不開放-->
                  <!-- <b-button size="xs" pill variant="danger" class="p-1 mr-2" @click="removeMember(customer)">
                    <i class="fa fa-times m-0"></i>
                  </b-button> -->
                  <i class="fa fa-angle-right" aria-hidden="true"></i>
                </div>
              </b-list-group-item>
            </div>
          </b-list-group>

          <div class="profile" v-if="selectedMember">
            <button class="btn btn-link" @click="selectedMember = false">
              <i class="fa fa-angle-left" aria-hidden="true"></i> 回列表
            </button>
            <div class="text-center p-3">
              <b-avatar
                class="my-4"
                size="8rem"
                :src="customer.avatar_url"
              ></b-avatar>
              <div class="h4 p-2 text-left">
                <h6 class="mb-2">LINE 名稱：{{ customer.name }}</h6>

                <div class="take-type-categories">
                  <!-- 客戶帳號資料 -->
                  <div v-if="normalFields.length" class="take-type-category">
                    <h6 class="category-title"><span id="customer-info">客戶帳號資料 <span class="mdi mdi-help-circle-outline"></span></span></h6>
                    <b-tooltip target="customer-info" placement="top">
                      此區內容為共用資訊，其他人也能看到
                    </b-tooltip>
                    <div class="category-options-row">
                      <h6 class="m-0" v-for="(field, index) in normalFields" :key="'field' + index">
                        {{field.title}}{{ field.required ? ' *' : '' }}：<SaveText :status="saveStatus[field.matched_key]" />
                        <span v-if="field.type === 'checkbox' || field.type === 'tags'">{{ formatValue(getValue(field)) }}</span>
                        <span v-else :style="{ whiteSpace: field.type === 'textarea' ? 'pre-line' : 'normal' }">{{ getValue(field) }}</span>
                        <button
                          class="btn m-0"
                          v-b-modal.modal-edit-field
                          @click="openEditField(field)"
                          :hidden="!$permissions.has($permissions.consts.CHATROOM_LIST_CHAT_MODIFY_CUSTOMER_PROFILE)">
                        <i class="fa fa-pencil" aria-hidden="true"></i>
                        </button>
                      </h6>
                    </div>
                  </div>

                  <!-- 我的私人備註 -->
                  <div v-if="staffExtraFields.length" class="take-type-category">
                    <h6 class="category-title"><span id="my-personal-remark">我的私人備註 <span class="mdi mdi-help-circle-outline"></span></span></h6>
                    <b-tooltip target="my-personal-remark" placement="top">
                      此區為您的個人備註，內容不會對他人公開
                    </b-tooltip>
                    <div class="category-options-row">
                      <h6 class="m-0" v-for="(field, index) in staffExtraFields" :key="'field' + index">
                        {{field.title}}：<SaveText :status="saveStatus[field.matched_key]" />
                        <span v-if="field.type !== 'checkbox' && field.type !== 'tags'">{{ getValue(field) }}</span>
                        <span v-else>{{ formatValue(getValue(field)) }}</span>

                        <button
                          class="btn m-0"
                          v-b-modal.modal-edit-field
                          @click="openEditField(field)"
                          :hidden="
                          !$permissions.has(
                            $permissions.consts
                              .CHATROOM_LIST_CHAT_MODIFY_CUSTOMER_PROFILE
                          )
                        "
                        >
                          <i class="fa fa-pencil" aria-hidden="true"></i>
                        </button>
                      </h6>
                    </div>
                  </div>

                  <div class="take-type-category">
                    <h6 class="category-title">標籤<SaveText :status="saveStatus.tags"/></h6>
                    <div class="category-options-row">
                      <b-badge
                        v-for="tag in orderedTags"
                        :key="tag"
                        variant="dark"
                        class="m-1"
                      >{{ tag }}</b-badge
                      >
                      <button
                        class="btn"
                        v-b-modal.modal-tags
                        :hidden="
                      !$permissions.has(
                        $permissions.consts
                          .CHATROOM_LIST_CHAT_MODIFY_CUSTOMER_PROFILE
                      )
                    "
                      >
                        <i class="fa fa-pencil" aria-hidden="true"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <b-list-group
              id="accordionExample"
              flush
              class="border-top border-bottom accordion"
            >
              <b-list-group-item role="button">
                  <button
                    class="btn btn-success"
                    @click="handleClickSaveCustomer"
                    :hidden="
                      !$permissions.has(
                        $permissions.consts
                          .CHATROOM_LIST_CHAT_MODIFY_CUSTOMER_PROFILE
                      )
                    "
                  >
                    儲存
                  </button>
              </b-list-group-item>
            </b-list-group>
          </div>
        </b-tab>
        <b-tab
          title="檔案"
          title-item-class="px-2"
          @click="fetchFileList"
          v-if="showFileListInDashboard"
        >
          <b-list-group>
            <div class="text-center mt-3 mb-3 loading-spinner" v-if="loading">
              <b-spinner variant="secondary" small />
            </div>
            <div v-else>
              <b-list-group-item
                class="d-flex align-items-center border-0"
                v-for="(file, i) in fileList"
                :key="i"
                :href="catFileType(file.mime) === 'jpg'? '': file.url"
              >
                <img
                  v-if="catFileType(file.mime) === 'csv'"
                  class="mr-2 file-type-icon" src="@/assets/images/file_type_icons/csv.png"
                >
                <img
                  v-if="catFileType(file.mime) === 'doc'"
                  class="mr-2 file-type-icon" src="@/assets/images/file_type_icons/doc.png"
                >
                <img
                  v-if="catFileType(file.mime) === 'file'"
                  class="mr-2 file-type-icon" src="@/assets/images/file_type_icons/file.png"
                >
                <img
                  v-if="catFileType(file.mime) === 'jpg'"
                  class="mr-2 file-type-icon" :src="file.url"
                  @click="currentImg = file.url"
                  v-b-modal.modal-showImg
                >
                <img
                  v-if="catFileType(file.mime) === 'pdf'"
                  class="mr-2 file-type-icon" src="@/assets/images/file_type_icons/pdf.png"
                >
                <img
                  v-if="catFileType(file.mime) === 'ppt'"
                  class="mr-2 file-type-icon" src="@/assets/images/file_type_icons/ppt.png"
                >
                <img
                  v-if="catFileType(file.mime) === 'txt'"
                  class="mr-2 file-type-icon" src="@/assets/images/file_type_icons/txt.png"
                >
                <img
                  v-if="catFileType(file.mime) === 'xls'"
                  class="mr-2 file-type-icon" src="@/assets/images/file_type_icons/xls.png"
                >
                <img
                  v-if="catFileType(file.mime) === 'zip'"
                  class="mr-2 file-type-icon" src="@/assets/images/file_type_icons/zip.png"
                >

                <div class="d-flex flex-column lh-sm-0 text-truncate">
                  <div class="mr-auto font-weight-bold">
                    {{ file.name }}
                  </div>
                  <div class="small text-muted">{{ file.sender.name }}</div>
                  <div class="small text-muted">{{ formatDateD(file.created_at) }}</div>
                </div>
              </b-list-group-item>
            </div>
          </b-list-group>
          <b-pagination
            class="separated mb-md-0 align-self-center py-3"
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            @change="handleChangePage"
          ></b-pagination>
        </b-tab>
      </b-tabs>
    </div>

</template>

<script>
import ChatroomAddMember from "@/pages/Dashboard/Chatrooms/ChatroomAddMember.vue";
import chatroomApi from "@/apis/chatrooms";
import customerApi from "@/apis/customer";
import store from "@/store";
import format from "date-fns/format";
import tagsMixin from "@/mixins/Dashboard/tags";
import _ from "lodash";
import moduleApi from "@/apis/module";
import SaveText from "@/components/SaveText.vue";
import Datepicker from "vuejs-datepicker";
import {zh} from "vuejs-datepicker/dist/locale";
import getHours from "date-fns/getHours/index";
import getMinutes from "date-fns/getMinutes/index";
import { validateOneField, FieldDataModel } from "@/utils/validation/index.js";

export default {
  mixins: [tagsMixin],
  components: {
    SaveText,
    ChatroomAddMember,
    Datepicker,
  },
  props: {
    selectedChatroom: {
      type: Object,
    },
    chatroomConfig: {
      type: Object,
    },
    showMemberListInDashboard: {
      type: Boolean,
    },
    showFileListInDashboard: {
      type: Boolean,
    },
  },
  watch: {
    async selectedChatroom() {
      if (!this.selectedChatroom?.id) return
      this.fetchAll()
    },
  },
  data() {
    return {
      zh,
      // data
      staffs: [],
      customers: [],
      selectedMember: false,
      fileList: [],
      tags: [],
      tagsWithoutDuplicate: [],
      customerProfile: {},
      inputNickname: null,
      inputTag: null,
      customer: {
        profiles: {},
        staff_profiles: {},
      },
      currentImg: "",
      sexualOptions: [
        { text: '男', value: 'male' },
        { text: '女', value: 'female' },
        { text: '未設定', value: null },
      ],

      // 動態欄位編輯狀態
      editingField: null,
      editingValue: null,

      // flags
      loading: false,

      // files pagination
      currentPage: 1,
      total: 0,
      perPage: 10,
      fields: [],
      staffExtraFields: [],
      normalFields: [],
      saveStatus: {
        tags: ''
      },
      validationErrorMessage: null,
    }
  },
  computed: {
    staffId() {
      return store.state.auth.user.id;
    },
    canShowAddMemberButton() {
      if (!this.chatroomConfig) return false;
      const sender = this.selectedChatroom?.senders.find(
        (s) => s.id === this.staffId
      );
      if (!sender) return false;
      return (
        this.chatroomConfig.invite_staff_in_dashboard_permission_role === sender.role &&
        (this.chatroomConfig.allow_invite_staff_in_dashboard ?? false)
      );
    },
    // 是否 disabled 加入成員的按鈕
    disabledAddMemberButton() {
      if (!this.chatroomConfig) return true;

      const matchRoles = this.selectedChatroom?.senders.filter(
        (s) => s.role === this.chatroomConfig.staff_invite_default_role
      );

      // 如果符合 default_role 的人數大於等於最高邀請上限，則 disabled
      return matchRoles.length >= this.chatroomConfig.staff_invite_max_count
    },
    canShowRemoveMemberButton() {
      if (!this.chatroomConfig) return false;

      const sender = this.selectedChatroom?.senders.find(
        (s) => s.id === this.staffId
      );

      if (!sender) return false;

      return (
        this.chatroomConfig.invite_staff_in_dashboard_permission_role === sender.role &&
        (this.chatroomConfig.allow_invite_staff_in_dashboard ?? false)
      );
    },
  },
  methods: {
    async fetchAll() {
      this.loading = true

      await Promise.all([
        this.fetchFileList(),
        this.fetchCustomers(),
        this.fetchStaffs(),
        this.resetCurrentCustomer(),
      ])

      this.loading = false
    },
    handleChangePage() {
      this.$nextTick(() => {
        this.fetchFileList();
      });
    },
    async fetchFileList() {
      try {
        let params = {
          page: this.currentPage,
          per_page: this.perPage,
        };
        const { data } = await chatroomApi.getFileList({
          chatroom: this.selectedChatroom,
          params: params,
        });
        this.fileList = data.data;
        this.total = data.meta.total;
        this.perPage = data.meta.per_page;
      } catch (e) {
        console.error(e);
      }
    },
    catFileType(fileType) {
      let type = "file";

      const jpegTypes = ["jpeg", "jpg", "png"];
      jpegTypes.forEach((jpegType) => {
        fileType.includes(jpegType) ? (type = "jpg") : "";
      });

      const docTypes = ["doc", "docx"];
      docTypes.forEach((docType) => {
        fileType.includes(docType) ? (type = "doc") : "";
      });

      const pdfTypes = ["pdf"];
      pdfTypes.forEach((pdfType) => {
        fileType.includes(pdfType) ? (type = "pdf") : "";
      });

      const csvTypes = ["csv"];
      csvTypes.forEach((csvType) => {
        fileType.includes(csvType) ? (type = "csv") : "";
      });

      const pptTypes = ["ppt", "pptx"];
      pptTypes.forEach((pptType) => {
        fileType.includes(pptType) ? (type = "ppt") : "";
      });

      const txtTypes = ["txt"];
      txtTypes.forEach((txtType) => {
        fileType.includes(txtType) ? (type = "txt") : "";
      });

      const xlsTypes = ["xls", "xlsx"];
      xlsTypes.forEach((xlsType) => {
        fileType.includes(xlsType) ? (type = "xls") : "";
      });

      const zipTypes = ["zip"];
      zipTypes.forEach((zipType) => {
        fileType.includes(zipType) ? (type = "zip") : "";
      });

      return type;
    },
    formatDateD(value) {
      if (value) {
        return format(new Date(value), 'yyyy-MM-dd HH:mm');
      }
    },
    async fetchCustomers() {
      const { data } = await chatroomApi.getCustomers(this.selectedChatroom);
      this.customers = data.data;
    },
    async fetchStaffs() {
      const { data } = await chatroomApi.getStaffs(this.selectedChatroom);
      this.staffs = data.data;
    },
    async removeMember(member) {
      let confirm = await this.$swal.fire({
        type: "warning",
        title: "從聊天室移除",
        text: "您確認要將此成員自聊天室移除嗎？",
        confirmButtonText: "確認",
        cancelButtonText: "取消",
        showCancelButton: true,
      })

      if (!confirm.value) return

      try {
        await chatroomApi.removeMember({
          chatroom: this.selectedChatroom,
          params: {
            staff_id: member.id,
          }
        });

        this.$swal.fire("移除成員", "成功將成員自聊天室中移除", "success");
        this.fetchCustomers();
        this.fetchStaffs();
      } catch (error) {
        console.error(error);
        let message = "失敗";

        if (error.response.data.message) {
          message = error.response.data.message;
        }

        this.$swal.fire("移除成員", message, "danger");
      }
    },
    handleSelectMember(customerId) {
      const me = this;
      const promise1 = this.fetchCustomer(customerId);
      const promise2 = this.fetchCustomerProfile(customerId);
      const promise3 = this.fetchModuleConfig();
      this.fetchCustomerTags(customerId);
      Promise.all([promise1, promise2, promise3]).then(function () {
        if (!me.customerProfile.nickname) {
          me.customerProfile.nickname = me.customer.name;
        }
        me.inputNickname = me.customerProfile.nickname;
        me.$forceUpdate();
      });
    },
    async fetchCustomer(customerId) {
      const { data } = await customerApi.getCustomer(customerId,  { module_code: 'chatroom' });

      _.assign(this.customer, data);
      this.customer.language = this.customer.data?.language;
      this.selectedMember = true;
    },
    async fetchModuleConfig() {
      let { data: { member_fields = [] } } = await moduleApi.getModuleConfig({ module_code: "chatroom" });
      const sortedFields = _.sortBy(member_fields, "order");

      this.staffExtraFields = sortedFields.filter(f => f.matched_key?.startsWith("staff_extra."));
      this.normalFields     = sortedFields.filter(f => !f.matched_key?.startsWith("staff_extra."));

      this.staffExtraFields.forEach(field => {
        this.$set(this.saveStatus, field.matched_key, "")
      })

      this.normalFields.forEach(field => {
        this.$set(this.saveStatus, field.matched_key, "")
      })
    },
    async fetchCustomerProfile(customerId) {
      const { data } = await customerApi.getCustomerProfiles(customerId);
      this.customerProfile = data.data;
    },

    async fetchCustomerTags(customerId) {
      const { data } = await customerApi.getCustomerTags(customerId);
      this.tags = data.data.map((t) => {
        return t.name;
      }).sort((a, b) => a.localeCompare(b));
      this.tagsWithoutDuplicate = [...new Set(this.tags)];
      this.saveStatus.tags = '';
    },

    resetCurrentCustomer() {
      this.selectedMember = false
      _.assign(this.customer, {});
      this.customerProfile.nickname = null;
      this.customerProfile.name = null;
      this.customerProfile.personal_id = null;
      this.customerProfile.note = null;
      this.inputTag = null;
      this.inputNickname = null;
      this.editingField = null;
      this.editingValue = null;
    },

    openEditField(field) {
      this.editingField = field;
      console.log(this.editingField);
      const value = this.getValue(field);
      try {
        if (field.type === 'checkbox' && typeof value === 'string') {
          const parsed = JSON.parse(value);
          this.editingValue = Array.isArray(parsed) ? parsed : [];
        } else if (field.type === 'tags' && typeof value === 'string') {
          const parsed = JSON.parse(value);
          this.editingValue = Array.isArray(parsed) ? parsed : [];
        } else {
          this.editingValue = value ?? (field.type === 'checkbox' || field.type === 'tags' ? [] : null);
        }
      } catch (e) {
        this.editingValue = (field.type === 'checkbox' || field.type === 'tags') ? [] : value;
      }
      this.$nextTick(() => this.$forceUpdate());
    },

    handleEditingFieldChanged() {
      this.validationErrorMessage = null;
    },

    async handleSaveEditingField(bvModalEvent) {
      this.loading = true;
      if (!this.editingField) return;
      const key = this.editingField.matched_key;

      let value = this.editingValue;
      let payload = {
        key: key,
        value: this.editingValue
      };

      const validation = validateOneField(
        ['regex', 'required'],
        new FieldDataModel(key, this.editingField, value)
      );
      if (false === validation.pass) {
        this.validationErrorMessage = validation.errors.join(', ')
        this.loading = false;
        bvModalEvent.preventDefault(); // NOTE: prevent modal from hiding
        return;
      }

      if (this.editingField.type === 'date') {
        value = value.toISOString().slice(0, 10); // "2025-11-13"
        payload.value = value;
      } else if (this.editingField.type === 'tags' || this.editingField.type === 'checkbox') {
        value = this.formatValue(value);
      }

      // 根據 key 類型更新
      if (key.startsWith('extra.')) {
        const subKey = key.replace(/^extra\./, '');
        this.$set(this.customer.profiles, subKey, value);
      } else if (key.startsWith('staff_extra.')) {
        const subKey = key.replace(/^staff_extra\./, '');
        this.$set(this.customer.staff_profiles, subKey, value);
      } else {
        this.$set(this.customer, key, value);
      }

      this.$set(this.saveStatus, this.editingField.matched_key, "saving")

      try {
        await customerApi.storeCustomerDetails(this.customer.id, payload);
        this.handleSelectMember(this.customer.id);
        this.loading = false;
        this.$set(this.saveStatus, this.editingField.matched_key, "saved")
      } catch (err) {
        console.error(err);
        this.$set(this.saveStatus, this.editingField.matched_key, "failed")
        this.$swal.fire('失敗', '儲存失敗，請稍後重試', 'error');
        this.loading = false;
      } finally {
        this.editingField = null;
        this.editingValue = null;
      }
    },

    async handleUpdateCustomerProfile() {
      await customerApi.storeCustomerProfiles(this.customer.id, {
        ...this.customerProfile,
        nickname: this.inputNickname,
      });

      await this.fetchCustomerProfile(this.customer.id);
      // await this.fetchCustomers() //看有沒有要及時改顯示留言再決定要打開與否
      this.inputNickname = this.customerProfile.nickname;
      this.$nextTick(() => {
        this.$emit("updateMessageNickname", {
          sender_id: this.customer.id,
          nickname: this.inputNickname,
        });
      });
    },
    formatToDateObject(datetime) {
      if (!datetime) {
        return {}
      }
      return {
        date: new Date(datetime),
        time: {
          HH: String(getHours(new Date(datetime))).padStart(2, "0"),
          mm: String(getMinutes(new Date(datetime))).padStart(2, "0"),
        },
      }
    },
    handleCloseUpdateNickname() {
      this.inputNickname = this.customerProfile.nickname;
    },

    async handleSaveTags() {
      this.saveStatus.tags = 'saving';

      let add_tags = this.tagsWithoutDuplicate.filter((tag) => !this.tags.includes(tag));
      let remove_tags = this.tags.filter((tag) => !this.tagsWithoutDuplicate.includes(tag));
      let payload = {
        add_tags: add_tags,
        remove_tags: remove_tags,
      };
      this.tags.push(...add_tags);
      this.tags = this.tags.filter(tag => !remove_tags.includes(tag));

      try{
        await customerApi.setCustomerTags(this.customer.id, payload);
        this.saveStatus.tags = 'saved';
      } catch (error) {
        this.saveStatus.tags = 'failed';
      }
      this.fetchCustomerTags(this.customer.id);
    },

    handleClickRemoveTag(index) {
      this.tagsWithoutDuplicate.splice(index, 1);
    },

    handleEnterInputTag() {
      if (!this.inputTag) {
        return;
      }

      if (this.tagsWithoutDuplicate.includes(this.inputTag)) {
        this.inputTag = null;
        return;
      }

      this.tagsWithoutDuplicate.push(this.inputTag);
      this.inputTag = null;
    },
    // 逗號也要當作tag 分隔
    handleKeyUpInputTag(event) {
      if (event.key == ",") {
        this.inputTag = this.inputTag.slice(0, -1);
        this.handleEnterInputTag();
      }
    },
    async handleClickSaveCustomer() {
      let params = {
        mobile_phone: this.customer.mobile_phone,
        email: this.customer.email,
        birthday: this.customer.birthday,
        sexual: this.customer.sexual,
      };

      try {
        await customerApi.storeCustomer(this.customer.id, params);
        this.$swal.fire({
          icon: "success",
          text: "儲存成功",
        });
      } catch (err) {
        let errWording = "";
        for (let errKey in err.response.data.message) {
          errWording += err.response.data.message[errKey].join(",");
        }
        this.$swal("失敗", errWording, "error");
      }
    },
    formatValue(val) {
      if (!val) return ''

      if (Array.isArray(val)) {
        return val.join('、')
      }

      if (typeof val === 'string') {
        try {
          const parsed = JSON.parse(val)
          if (Array.isArray(parsed)) {
            return parsed.join('、')
          }
        } catch (e) {
          // 不是 JSON 格式就當普通字串
        }
      }

      return val
    },
    getValue(field) {
      let key = field.matched_key;
      if (!key) return '';

      let value = '';

      if (key.startsWith('extra.')) {
        key = key.replace(/^extra\./, '');
        value = this.customer?.profiles?.[key] ?? '';
      } else if (key.startsWith('staff_extra.')) {
        key = key.replace(/^staff_extra\./, '');
        value = this.customer?.staff_profiles?.[key] ?? '';
      } else {
        value = this.customer?.[key] ?? '';
      }

      // 🪄 特殊處理：textarea 要換行顯示
      if (field.type === 'textarea' && typeof value === 'string') {
        value = value.split(' ').join('\n');
      }

      return value;
    }
  }
}
</script>

<style lang="scss" scoped>

.gray-mask {
    -webkit-filter: grayscale(100%);
    -moz-filter: grayscale(100%);
    -ms-filter: grayscale(100%);
    -o-filter: grayscale(100%);
    filter: grayscale(100%);
    filter: gray;
}

.take-type-categories {
  .take-type-category {
    margin-bottom: 1.5rem;
    padding: 1rem;
    border: 1px solid #e0e0e0;
    border-radius: 5px;
    background-color: #f9f9f9;

    .category-title {
      margin-bottom: 0.75rem;
      font-weight: bold;
      color: #495057;
      border-bottom: 1px solid #dee2e6;
      padding-bottom: 0.5rem;
    }

    .category-options-row {
      display: block;
      flex-wrap: wrap;
      gap: 1rem;
      align-items: center;

      .custom-radio {
        margin-right: 0;
        margin-bottom: 0;
      }

      h6 {
        display: block;
        margin-bottom: 1rem; /* 自行控制間距 */
      }
    }
  }
}
</style>
